/**
 * Audit log page. Shows recent control-plane + proxied mutations on this
 * instance. SuperAdmin sees all events; TenantAdmin sees only their own.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingRow, EmptyState, ErrorState } from "@/components/common/StateViews";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { auditApi } from "@/lib/api";
import { ApiException } from "@/lib/api-client";

const RANGES = [1, 7, 30, 90];

export function AuditPage() {
  const [days, setDays] = useState(7);
  const { data, isLoading, error } = useQuery({
    queryKey: ["audit", days],
    queryFn: () => auditApi.list(days),
  });

  const events = data?.events ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Audit Log"
        description="Mutations and auth events on this instance. Retained for 90 days."
        actions={
          <div className="flex items-center gap-1 rounded-md border p-0.5">
            {RANGES.map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                  days === d ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"
                }`}
              >
                {d === 1 ? "24h" : `${d}d`}
              </button>
            ))}
          </div>
        }
      />

      {isLoading ? (
        <LoadingRow cols={5} />
      ) : error ? (
        <ErrorState message={error instanceof ApiException ? error.message : "Failed to load audit log"} />
      ) : events.length === 0 ? (
        <EmptyState message="No audit events in this range." />
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Method · Path</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map((e) => (
                <TableRow key={e.eventId}>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                    {new Date(e.occurredAt).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="font-mono text-xs">{e.action}</Badge>
                  </TableCell>
                  <TableCell className="text-sm">{e.actorUsername ?? e.actorUserId ?? "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground font-mono">
                    {e.method} {e.path}
                  </TableCell>
                  <TableCell>
                    <span className={`text-xs font-mono ${e.status >= 400 ? "text-destructive" : "text-muted-foreground"}`}>
                      {e.status}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
